'use client';

import { useState } from 'react';
import Image from 'next/image';
import camera_pink from '@/assets/images/camera_pink.png';
import slide1 from '@/assets/images/slide_image1.png';
import slide2 from '@/assets/images/slide_image2.png';
import slide3 from '@/assets/images/slide_image3.png';
import slide4 from '@/assets/images/slide_image4.png';
import slide5 from '@/assets/images/slide_image5.png';
import { useRouter } from "next/navigation";

export default function HeroSection({ config, theme }) {
  const variant = theme.variant || 'default';

  switch (variant) {
    case 'modern':
      return <ModernHero config={config} theme={theme} />;
    case 'elegant':
      return <ElegantHero config={config} theme={theme} />;
    default:
      return <DefaultHero config={config} theme={theme} />;
  }
}

function DefaultHero({ config, theme }) {
  const {
    title,
    subtitle,
    backgroundColor,
    textColor,
    buttonText
  } = config;

  const [activeThumbnail, setActiveThumbnail] = useState(0);
  const thumbnails = [slide1, slide2, slide3, slide4, slide5];
  const router = useRouter();
  return (
    <section
      style={{
        backgroundColor: backgroundColor || theme.secondaryColor || '#f3dce3',
        color: textColor || theme.primaryColor || '#6b2b52',
        padding: '4rem 2rem',
        minHeight: '85vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: '3rem',
          alignItems: 'center',
          width: '100%'
        }}
        className="hero-container"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div style={{ position: 'relative' }}>
            <div
              style={{
                width: '100%',
                aspectRatio: '16/10',
                borderRadius: '40px',
                overflow: 'hidden',
                boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                position: 'relative'
              }}
            >
              <Image
                src={thumbnails[activeThumbnail]}
                alt="Active Frame"
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                style={{ objectFit: 'cover' }}
                priority
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '80px',
                  height: '80px',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed rgba(255,255,255,0.5)',
                  padding: '5px'
                }}
              >
                <div style={{
                  width: '0',
                  height: '0',
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderLeft: '25px solid white',
                  marginLeft: '8px'
                }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', padding: '0 10px' }}>
            {thumbnails.map((thumb, idx) => (
              <div
                key={idx}
                onClick={() => setActiveThumbnail(idx)}
                className={`hero-thumbnail ${activeThumbnail === idx ? 'active' : ''}`}
                style={{
                  flex: 1,
                  aspectRatio: '1/0.7',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  position: 'relative',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                }}
              >
                <Image
                  src={thumb}
                  alt={`Thumbnail ${idx}`}
                  fill
                  sizes="(max-width: 768px) 20vw, 10vw"
                  style={{ objectFit: 'cover' }}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: '1.5rem'
        }}>
          <div style={{ width: '100%', maxWidth: '320px', position: 'relative', height: '200px' }}>
            <Image
              src={camera_pink}
              alt="Magic Camera"
              width={250}
              height={200}
              style={{ objectFit: 'contain' }}
            />
          </div>

          <div style={{ maxWidth: '450px' }}>
            <h1 style={{
              fontSize: '2.2rem',
              fontWeight: '700',
              lineHeight: '1.3',
              marginBottom: '1rem',
              color: theme.headingColor || '#7b506f'
            }}>
              {title} <span style={{ color: '#1a237e' }}>{subtitle}</span>
            </h1>
          </div>

          <button
            className="hero-button"
            onClick={() => {
              if (typeof window !== "undefined") { window.location.href = "/price"; }
            }}
            style={{
              backgroundColor: theme.buttonColor || '#7b506f',
              color: theme.buttonTextColor || '#fff',
              border: 'none',
              padding: '1rem 3rem',
              borderRadius: theme.buttonBorderRadius || '25px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: '600',
              boxShadow: '0 6px 15px rgba(123, 80, 111, 0.4)',
              marginTop: '1rem'
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>

      <style jsx>{`
        .hero-thumbnail { transition: transform 0.2s ease; border: 3px solid transparent; }
        .hero-thumbnail:hover { transform: scale(1.05); }
        .hero-thumbnail.active { border-color: ${theme.primaryColor}; }
        .hero-button { transition: all 0.3s ease; }
        .hero-button:hover { transform: translateY(-3px); background-color: ${theme.buttonHoverColor || '#8b3a6a'}; }
        @media (max-width: 960px) {
          .hero-container { grid-template-columns: 1fr !important; gap: 4rem !important; }
          .hero-container > div:first-child { order: 2; }
          .hero-container > div:last-child { order: 1; }
        }
      `}</style>
    </section>
  );
}

function ModernHero({ config, theme }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const thumbnails = [slide1, slide2, slide3, slide4, slide5];

  return (
    <section className="modern-hero-section" style={{
      backgroundColor: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <div style={{ textAlign: 'center' }} className="modern-hero-header">
        <h1 style={{
          fontFamily: theme.logoTextFontFamily || 'Elsie Swash Caps, serif',
          color: '#2d2d2d',
          fontWeight: '400'
        }} className="modern-hero-title">
          From Moments To Memories You Can Hold.
        </h1>
        <p style={{
          color: '#999',
          fontWeight: '500',
          letterSpacing: '0.5px'
        }} className="modern-hero-subtitle">
          Personalize Photo Frames, Calendars, Flyers And More — Turn Your Everyday Moments Into Gifts.
        </p>
      </div>

      <div className="album-card-container">
        <div className="album-card-leather">
          <div className="album-inner-content">
            <div className="album-binder">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="binder-ring" />
              ))}
            </div>

            <div className="album-page image-page">
              <div className="image-wrapper">
                <Image
                  src={thumbnails[activeIndex]}
                  alt="SlideContent"
                  fill
                  style={{ objectFit: 'cover' }}
                />
                <div className="play-button-overlay">
                  <div className="play-icon" />
                </div>
              </div>
            </div>

            <div className="album-page text-page">
              <div className="text-page-inner-border" />

              <h2 className="text-page-title">
                Magic Wedding Frame
              </h2>
              <p className="text-page-desc">
                A Photo That Holds More Than A Moment.<br />
                Scan The Frame To Relive The Wedding Memories In Motion.
              </p>

              <div className="text-page-icons">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M7 11.5V14m0-2.5v-6a3 3 0 1 1 6 0v6a3 3 0 1 1-6 0z" />
                  <path d="M10 17v2m0-2a5 5 0 0 1-5-5v-.5M15 11.5v.5a5 5 0 0 1-5 5" />
                </svg>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="9" cy="9" r="5" />
                  <circle cx="15" cy="15" r="5" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
        {thumbnails.map((_, i) => (
          <div
            key={i}
            onClick={() => setActiveIndex(i)}
            style={{
              width: i === activeIndex ? '20px' : '8px',
              height: '8px',
              borderRadius: '4px',
              backgroundColor: i === activeIndex ? '#4a0404' : '#ddd',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          />
        ))}
      </div>

      <button className="modern-hero-cta">
        Scan With Magic Camera
      </button>

      <style jsx>{`
        .modern-hero-section {
          padding: 3rem 2rem;
          min-height: 85vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 2rem;
        }
        .modern-hero-header {
          max-width: 1200px;
          margin-bottom: 0;
        }
        .modern-hero-title {
          font-size: 2.8rem;
          margin-bottom: 0.75rem;
          line-height: 1.1;
          font-weight: 400;
        }
        .modern-hero-subtitle {
          font-size: 1rem;
          max-width: 900px;
          margin: 0 auto;
        }
        .album-card-container {
          width: 100%;
          max-width: 1000px;
          perspective: 2000px;
        }
        .album-card-leather {
          width: 100%;
          aspect-ratio: 16/8.5;
          background-color: #33022F;
          padding: 12px;
          border-radius: 12px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.25);
          position: relative;
        }
        .album-inner-content {
          width: 100%;
          height: 100%;
          background-color: #fff;
          display: flex;
          position: relative;
          overflow: hidden;
          border-radius: 4px;
        }
        .album-page {
          flex: 1;
          height: 100%;
          position: relative;
        }
        .image-page {
          padding: 12px;
        }
        .image-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border-radius: 4px;
          box-shadow: inset 0 0 10px rgba(0,0,0,0.1);
        }
        .play-button-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 70px;
          height: 70px;
          background-color: rgba(0,0,0,0.4);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: 2px solid rgba(255,255,255,0.7);
          z-index: 5;
        }
        .play-icon {
          width: 0; 
          height: 0;
          border-top: 15px solid transparent;
          border-bottom: 15px solid transparent;
          border-left: 24px solid white;
          margin-left: 8px;
        }
        .album-binder {
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 50px;
          transform: translateX(-50%);
          z-index: 10;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          align-items: center;
          padding: 20px 0;
        }
        .binder-ring {
          width: 100%;
          height: 10px;
          background: linear-gradient(to bottom, #33022F, #1a0118, #33022F);
          border-radius: 5px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          border: 1px solid #AA8A2E;
        }
        .text-page {
          background-color: #33022F;
          margin: 12px 12px 12px 0;
          padding: 2rem;
          color: #fff;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          border-radius: 4px;
        }
        .text-page-inner-border {
          position: absolute;
          inset: 15px;
          border: 1px solid rgba(212, 175, 55, 0.4);
          pointer-events: none;
        }
        .text-page-title {
          font-family: 'Elsie Swash Caps, serif';
          color: #D4AF37;
          font-size: 2rem;
          margin-bottom: 1rem;
          line-height: 1.2;
        }
        .text-page-desc {
          font-size: 1.1rem;
          line-height: 1.4;
          max-width: 320px;
          font-weight: 300;
          color: #FDECC8;
        }
        .text-page-icons {
          margin-top: 2rem;
          display: flex;
          justify-content: center;
          gap: 2.5rem;
          color: #D4AF37;
        }
        .modern-hero-cta {
          background-color: #33022F;
          color: #fff;
          padding: 1.2rem 3.5rem;
          border-radius: 8px;
          border: 1px solid #D4AF37;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 10px 20px rgba(51, 2, 47, 0.3);
          margin-top: 0.5rem;
          transition: all 0.3s;
        }
        .modern-hero-cta:hover {
          background-color: #1a0118;
          transform: translateY(-3px);
          box-shadow: 0 15px 30px rgba(51, 2, 47, 0.4);
        }

        @media (max-width: 960px) {
           .modern-hero-title { font-size: 2.2rem; }
           .album-card-container { max-width: 90%; }
        }

        @media (max-width: 768px) {
          .modern-hero-section {
            padding: 3rem 1rem;
            min-height: auto;
          }
          .modern-hero-title {
            font-size: 2rem;
          }
          .album-card-leather {
            aspect-ratio: 16/10;
          }
          .text-page {
            display: none;
          }
          .album-binder {
            left: 20px;
            transform: none;
            width: 30px;
          }
          .binder-ring {
            height: 8px;
          }
          .image-page {
            padding-left: 50px;
          }
        }
      `}</style>
    </section>
  );
}

function ElegantHero({ config, theme }) {
  const { title, subtitle, buttonText } = config;
  const [activeThumbnail, setActiveThumbnail] = useState(0);
  const thumbnails = [slide1, slide2, slide3, slide4, slide5];

  // Design constants
  const goldColor = '#C5A048';
  const darkWood = '#3B2414';
  const textColor = '#3B2414';

  return (
    <section
      style={{
        backgroundColor: '#fff',
        padding: '4rem 2rem',
        minHeight: '85vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
      className="elegant-hero-section"
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: '3rem',
          alignItems: 'center',
          width: '100%'
        }}
        className="elegant-hero-container"
      >
        {/* Left Side: Photo Frame & Thumbnails (Order: 2 on Mobile) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }} className="elegant-hero-visuals">

          {/* Main Hanging Frame */}
          <div className="main-hanging-frame" style={{
            position: 'relative',
            width: '100%',
            maxWidth: '600px',
            marginBottom: '2.5rem'
          }}>
            {/* Hanging String */}
            <div style={{ width: '100%', height: '80px', position: 'relative', marginBottom: '-5px' }}>
              <div style={{
                position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                width: '12px', height: '12px', backgroundColor: darkWood, borderRadius: '50%', zIndex: 3
              }} />
              <svg width="100%" height="100" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path d="M50 0 L15 100 L85 100 Z" fill="none" stroke={darkWood} strokeWidth="1" />
              </svg>
            </div>

            {/* Wooden Frame */}
            <div style={{
              width: '100%', aspectRatio: '16/10', backgroundColor: darkWood, padding: '12px',
              borderRadius: '6px', boxShadow: '0 25px 60px rgba(0,0,0,0.2)', position: 'relative',
              zIndex: 2
            }}>
              <div style={{
                width: '100%', height: '100%', backgroundColor: '#fff', position: 'relative',
                overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)'
              }}>
                <Image
                  src={thumbnails[activeThumbnail]}
                  alt="Hero Magic Frame"
                  fill
                  style={{ objectFit: 'cover' }}
                  priority
                />

                {/* Play Button Overlay */}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  width: '90px', height: '90px', backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  border: '2px solid rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(2px)'
                }}>
                  <div style={{
                    width: '0', height: '0', borderTop: '20px solid transparent', borderBottom: '20px solid transparent',
                    borderLeft: '32px solid white', marginLeft: '10px'
                  }} />
                </div>
              </div>
            </div>
          </div>

          {/* Thumbnails Row */}
          <div className="elegant-thumbnails-row">
            {thumbnails.map((thumb, idx) => (
              <div
                key={idx}
                onClick={() => setActiveThumbnail(idx)}
                className={`elegant-thumb-item ${activeThumbnail === idx ? 'active' : ''}`}
                style={{ backgroundColor: darkWood }}
              >
                <div className="thumb-image-container">
                  <Image
                    src={thumb}
                    alt={`mini-frame-${idx}`}
                    fill
                    sizes="100px"
                    style={{ objectFit: 'cover' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Title & CTA (Order: 1 on Mobile) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2rem' }} className="elegant-hero-text">

          {/* Illustration Container */}
          <div style={{ width: '100%', maxWidth: '300px', height: '200px', position: 'relative' }}>
            <Image
              src={camera_pink}
              alt="Magic Illustration"
              fill
              style={{ objectFit: 'contain' }}
            />
          </div>

          {/* Styled Heading */}
          <h1 style={{
            fontSize: '2.4rem', fontWeight: '800', color: textColor, lineHeight: '1.25',
            fontFamily: theme.headingFontFamily || theme.fontFamily
          }}>
            {title} <br className="hide-mobile" />
            <span style={{ color: goldColor }}>{subtitle}</span>
          </h1>

          {/* Elegant Button */}
          <button style={{
            backgroundColor: darkWood, color: '#fff', padding: '1rem 3rem', borderRadius: '12px',
            border: 'none', fontSize: '1.1rem', fontWeight: '700', cursor: 'pointer',
            boxShadow: '0 10px 20px rgba(0,0,0,0.2)', transition: 'all 0.3s ease'
          }} className="elegant-shop-btn">
            {buttonText}
          </button>
        </div>

      </div>

      <style jsx>{`
        .elegant-shop-btn:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.3);
        }
        .elegant-thumbnails-row {
          display: flex;
          gap: 15px;
          justify-content: center;
          width: 100%;
          flex-wrap: wrap;
          padding: 0 10px;
        }
        .elegant-thumb-item {
          width: 90px;
          aspect-ratio: 1/0.7;
          padding: 3px;
          border-radius: 2px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
          border: 1px solid transparent;
        }
        .elegant-thumb-item.active {
          border: 2px solid ${goldColor};
          transform: scale(1.1) translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }
        .thumb-image-container {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
        }
        @media (max-width: 960px) {
          .elegant-hero-container { grid-template-columns: 1fr !important; gap: 4rem !important; }
          .elegant-hero-visuals { order: 2; }
          .elegant-hero-text { order: 1; }
          .elegant-hero-section { padding: 4rem 1rem !important; }
          .elegant-hero-heading { font-size: 2rem !important; }
          .hide-mobile { display: none; }
        }
        @media (max-width: 600px) {
          .elegant-thumb-item {
            width: 70px;
          }
          .elegant-thumbnails-row {
            gap: 10px;
          }
        }
      `}</style>
    </section>
  );
}
